/**
 * Company policies — the handbook: code of conduct, IT security, POSH, travel (ADR 0026).
 *
 * NOT the leave policies (`leave.contract.ts`, `/leave/policies`) and NOT the payroll company-policy
 * layer (ADR 0016). This is the document an employee reads and, when required, acknowledges.
 *
 * VERSIONED (invariant 5). A policy is an identity; everything an employee sees — title, category,
 * body, effective date, audience, whether acknowledgement is required — lives on a VERSION. Editing
 * a published policy edits its one DRAFT version; publishing the draft appends it to the timeline
 * and closes the previous version the day before the new one takes effect. Nothing published is
 * ever overwritten, so "what did the policy say in March, and who had acknowledged it" is a query.
 * Bringing back an older version's terms (`restoreVersion`) works the same way — it seeds the draft
 * from that version and publishing it appends a NEW version with that content, effective today. It
 * never rewrites the version it copied from.
 *
 * `setArchived` withdraws the WHOLE policy (every version, from everyone) — there is no per-version
 * withdraw, because on any given day only one version is ever in force, and a closed PAST version
 * is history that invariant 5 forbids erasing (it is the "who acknowledged what, when" record). The
 * one version a per-version action can meaningfully touch is a SCHEDULED one — published, but dated
 * after today, so it has never taken effect and nobody could ever have acknowledged it. That is what
 * `cancelScheduledVersion` deletes: the version disappears outright, and the version it would have
 * replaced regains an open end date so the policy keeps applying instead of lapsing on the date that
 * now never comes. To step away from the version CURRENTLY in force, restore an earlier one instead.
 *
 * AUDIENCE. Four lists — legal entities, locations, departments, employee groups. Empty everywhere
 * means the whole organisation. Otherwise every NON-EMPTY list must match (AND across kinds, OR
 * within one): "India + Engineering" is India's engineers, not everyone in either.
 *
 * PERMISSIONS. Employees read with `policy:read` and acknowledge with `policy:acknowledge`; every
 * `/company-policies/mine*` route answers only for the caller. Everything else is `policy:manage`,
 * checked against the whole organisation: a policy is org-wide configuration, like org settings.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import {
  dateOnlySchema,
  errorBodySchema,
  instantSchema,
  orgIdSchema,
  uuidSchema,
} from '../schemas/common.js';
import { routeMeta } from './meta.js';

const c = initContract();

// ---------------------------------------------------------------------------
// Shared shapes
// ---------------------------------------------------------------------------

/** Suggested in the editor; stored as free text so an org can add its own. */
export const COMPANY_POLICY_CATEGORIES = [
  'Code of conduct',
  'HR',
  'IT & security',
  'Health & safety',
  'Finance & travel',
  'Legal & compliance',
  'Benefits',
] as const;

/** `markdown` renders `bodyMarkdown` (raw HTML is never rendered); `pdf` shows the uploaded file. */
export const companyPolicyBodyFormatSchema = z.enum(['markdown', 'pdf']);
export type CompanyPolicyBodyFormat = z.infer<typeof companyPolicyBodyFormatSchema>;

export const companyPolicyVersionStatusSchema = z.enum(['draft', 'published']);

/** Derived, never stored: see `companyPolicySummarySchema.status`. */
export const companyPolicyStatusSchema = z.enum(['draft', 'scheduled', 'active', 'archived']);
export type CompanyPolicyStatus = z.infer<typeof companyPolicyStatusSchema>;

export const companyPolicyAudienceSchema = z.object({
  legalEntityIds: z.array(uuidSchema).max(200).default([]),
  locationIds: z.array(uuidSchema).max(200).default([]),
  departmentIds: z.array(uuidSchema).max(200).default([]),
  employeeGroupIds: z.array(uuidSchema).max(200).default([]),
});
export type CompanyPolicyAudience = z.infer<typeof companyPolicyAudienceSchema>;

/** The same audience with names resolved for display; an id whose target was deleted reads null. */
export const companyPolicyAudienceLabelsSchema = z.object({
  legalEntities: z.array(z.object({ id: uuidSchema, name: z.string().nullable() })),
  locations: z.array(z.object({ id: uuidSchema, name: z.string().nullable() })),
  departments: z.array(z.object({ id: uuidSchema, name: z.string().nullable() })),
  employeeGroups: z.array(z.object({ id: uuidSchema, name: z.string().nullable() })),
});

/** File metadata only. The storage key never leaves the server; bytes come from the download routes. */
export const companyPolicyDocumentSchema = z.object({
  fileName: z.string(),
  mimeType: z.string(),
  sizeBytes: z.number().int().nonnegative(),
});

export const companyPolicyVersionSchema = z.object({
  id: uuidSchema,
  policyId: uuidSchema,
  versionNumber: z.number().int().positive(),
  status: companyPolicyVersionStatusSchema,
  title: z.string(),
  category: z.string(),
  summary: z.string().nullable(),
  bodyFormat: companyPolicyBodyFormatSchema,
  bodyMarkdown: z.string().nullable(),
  document: companyPolicyDocumentSchema.nullable(),
  /** First day this version applies. */
  effectiveFrom: dateOnlySchema,
  /** Inclusive last day; null while it is the newest published version (or a draft). */
  validTo: dateOnlySchema.nullable(),
  requiresAcknowledgement: z.boolean(),
  /** Days after `effectiveFrom` (or publication, if later) by which to acknowledge; null = no deadline. */
  acknowledgementDueDays: z.number().int().positive().nullable(),
  audience: companyPolicyAudienceSchema,
  audienceLabels: companyPolicyAudienceLabelsSchema,
  changeNote: z.string().nullable(),
  publishedAt: instantSchema.nullable(),
  publishedByName: z.string().nullable(),
  createdAt: instantSchema,
  updatedAt: instantSchema,
});
export type CompanyPolicyVersion = z.infer<typeof companyPolicyVersionSchema>;

/** One row of a policy's history — no body, so the list stays small. */
export const companyPolicyVersionSummarySchema = companyPolicyVersionSchema.pick({
  id: true,
  versionNumber: true,
  status: true,
  title: true,
  effectiveFrom: true,
  validTo: true,
  requiresAcknowledgement: true,
  changeNote: true,
  publishedAt: true,
  publishedByName: true,
});

export const acknowledgementCountsSchema = z.object({
  applicable: z.number().int().nonnegative(),
  acknowledged: z.number().int().nonnegative(),
  pending: z.number().int().nonnegative(),
  /** Pending past the due date. Zero when the version has no deadline. */
  overdue: z.number().int().nonnegative(),
});

export const companyPolicySummarySchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  /** From the current version, else the scheduled one, else the draft. */
  title: z.string(),
  category: z.string(),
  /**
   * `draft` — never published. `scheduled` — published, but its first version is not in effect yet.
   * `active` — a published version is in effect today. `archived` — withdrawn from employees.
   */
  status: companyPolicyStatusSchema,
  currentVersion: companyPolicyVersionSummarySchema.nullable(),
  /** A published version dated after today, waiting to take over. */
  scheduledVersion: companyPolicyVersionSummarySchema.nullable(),
  hasDraft: z.boolean(),
  /** Shared by copies made together across a company group (ADR 0037); null for an ordinary policy. */
  groupLineageId: uuidSchema.nullable(),
  /** For the current version when it requires acknowledgement; null otherwise. */
  acknowledgements: acknowledgementCountsSchema.nullable(),
  updatedAt: instantSchema,
});
export type CompanyPolicySummary = z.infer<typeof companyPolicySummarySchema>;

export const companyPolicyDetailSchema = companyPolicySummarySchema.extend({
  draft: companyPolicyVersionSchema.nullable(),
  /** Every published version, newest first. */
  versions: z.array(companyPolicyVersionSummarySchema),
});
export type CompanyPolicyDetail = z.infer<typeof companyPolicyDetailSchema>;

/**
 * `cancelScheduledVersion`'s result: the policy afterwards, UNLESS the cancelled version was the
 * only one the policy ever had and nothing else was in progress — then there is nothing left of it
 * to show, and it is deleted outright (the same rule `discardDraft` applies to a never-published
 * policy). The web client checks `'deleted' in result` the same way it already does for that route.
 */
export const cancelScheduledVersionResultSchema = z.union([
  companyPolicyDetailSchema,
  z.object({ deleted: z.literal('policy') }),
]);
export type CancelScheduledVersionResult = z.infer<typeof cancelScheduledVersionResultSchema>;

/** One policy as the employee sees it: the version in effect for them today. */
export const myCompanyPolicySchema = z.object({
  policyId: uuidSchema,
  versionId: uuidSchema,
  versionNumber: z.number().int().positive(),
  title: z.string(),
  category: z.string(),
  summary: z.string().nullable(),
  effectiveFrom: dateOnlySchema,
  publishedAt: instantSchema.nullable(),
  requiresAcknowledgement: z.boolean(),
  acknowledgedAt: instantSchema.nullable(),
  /** Null when there is no deadline or nothing to acknowledge. */
  acknowledgementDueOn: dateOnlySchema.nullable(),
  /** Requires acknowledgement and the caller has not acknowledged THIS version. */
  pendingAcknowledgement: z.boolean(),
});
export type MyCompanyPolicy = z.infer<typeof myCompanyPolicySchema>;

export const myCompanyPolicyDetailSchema = myCompanyPolicySchema.extend({
  bodyFormat: companyPolicyBodyFormatSchema,
  bodyMarkdown: z.string().nullable(),
  document: companyPolicyDocumentSchema.nullable(),
  changeNote: z.string().nullable(),
});
export type MyCompanyPolicyDetail = z.infer<typeof myCompanyPolicyDetailSchema>;

export const companyPolicyFileSchema = z.object({
  fileName: z.string(),
  mimeType: z.string(),
  contentBase64: z.string(),
});

export const acknowledgementReportRowSchema = z.object({
  personId: uuidSchema,
  employmentId: uuidSchema,
  employeeCode: z.string(),
  name: z.string(),
  legalEntityName: z.string().nullable(),
  departmentName: z.string().nullable(),
  locationName: z.string().nullable(),
  acknowledgedAt: instantSchema.nullable(),
  overdue: z.boolean(),
});
export type AcknowledgementReportRow = z.infer<typeof acknowledgementReportRowSchema>;

// ---------------------------------------------------------------------------
// Requests
// ---------------------------------------------------------------------------

const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((value) => (value === '' ? null : value))
    .nullable()
    .optional();

/** A draft may be incomplete; `publishCompanyPolicy` is where completeness is enforced. */
export const companyPolicyDraftBodySchema = z.object({
  title: z.string().trim().min(1).max(200),
  category: z.string().trim().min(1).max(60),
  summary: optionalText(500),
  bodyFormat: companyPolicyBodyFormatSchema.default('markdown'),
  bodyMarkdown: optionalText(100_000),
  effectiveFrom: dateOnlySchema,
  requiresAcknowledgement: z.boolean().default(false),
  acknowledgementDueDays: z.number().int().min(1).max(365).nullable().optional(),
  audience: companyPolicyAudienceSchema.default({}),
});
export type CompanyPolicyDraftBody = z.infer<typeof companyPolicyDraftBodySchema>;

/** Base64 JSON like expense receipts (no multipart parser for one route). PDF only, ≤ 10 MB. */
export const uploadCompanyPolicyDocumentBodySchema = z.object({
  fileName: z.string().trim().min(1).max(255),
  mimeType: z.string().trim().min(1).max(100),
  contentBase64: z.string().min(1),
});

export const publishCompanyPolicyBodySchema = z.object({
  /** "What changed", shown in the history and to employees on the new version. */
  changeNote: optionalText(1000),
  /** Notify the audience now. Defaults on; a typo fix can go out quietly. */
  notify: z.boolean().default(true),
});

export const acknowledgeCompanyPolicyBodySchema = z.object({
  /** The version the caller read. A stale page acknowledging a superseded version gets 409. */
  versionId: uuidSchema,
});

const listQuerySchema = z.object({
  status: z.enum(['all', 'draft', 'scheduled', 'active', 'archived']).default('all'),
  category: z.string().trim().max(60).optional(),
});

const reportQuerySchema = z.object({
  status: z.enum(['all', 'acknowledged', 'pending']).default('all'),
});

// ---------------------------------------------------------------------------
// Contract
// ---------------------------------------------------------------------------

const manage = routeMeta('policy:manage');

export const companyPolicyContract = c.router(
  {
    // ---- Employee self-service (literal `/mine` paths first: see contract-route-order.test) ----
    listMyPolicies: {
      method: 'GET',
      path: '/mine',
      responses: {
        200: z.object({
          items: z.array(myCompanyPolicySchema),
          pendingCount: z.number().int().nonnegative(),
        }),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: 'Company policies that apply to me, with what I still have to acknowledge',
      metadata: routeMeta('policy:read'),
    },
    getMyPolicy: {
      method: 'GET',
      path: '/mine/:policyId',
      responses: {
        200: myCompanyPolicyDetailSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Read one company policy as it applies to me today',
      metadata: routeMeta('policy:read'),
    },
    downloadMyPolicyDocument: {
      method: 'GET',
      path: '/mine/:policyId/document',
      responses: {
        200: companyPolicyFileSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: "Download the PDF of a policy's current version",
      // A whole file as base64 is no use in a prompt; the copilot can navigate to the page instead.
      metadata: routeMeta('policy:read', { ai: 'hidden' }),
    },
    acknowledgeMyPolicy: {
      method: 'POST',
      path: '/mine/:policyId/acknowledge',
      body: acknowledgeCompanyPolicyBodySchema,
      responses: {
        200: myCompanyPolicySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** The version is no longer the one in effect for the caller. */
        409: errorBodySchema,
      },
      summary: 'Acknowledge that I have read the current version of a policy',
      metadata: routeMeta('policy:acknowledge'),
    },

    // ---- Administration ------------------------------------------------------------------------
    listPolicies: {
      method: 'GET',
      path: '/',
      query: listQuerySchema,
      responses: {
        200: z.object({ items: z.array(companyPolicySummarySchema) }),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: 'Every company policy with its status and acknowledgement progress',
      metadata: manage,
    },
    createPolicy: {
      method: 'POST',
      path: '/',
      body: companyPolicyDraftBodySchema,
      responses: {
        201: companyPolicyDetailSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Start a new company policy as a draft',
      metadata: manage,
    },
    getPolicy: {
      method: 'GET',
      path: '/:policyId',
      responses: {
        200: companyPolicyDetailSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'A company policy with its draft and version history',
      metadata: manage,
    },
    restoreVersion: {
      method: 'POST',
      path: '/:policyId/versions/:versionId/restore',
      body: z.object({}).default({}),
      responses: {
        200: companyPolicyDetailSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** A draft is already being edited — discard or publish it first. */
        409: errorBodySchema,
      },
      summary:
        'Replace the draft with a copy of an older published version (its PDF included), so it can be published again',
      metadata: manage,
    },
    cancelScheduledVersion: {
      method: 'DELETE',
      path: '/:policyId/versions/:versionId',
      responses: {
        200: cancelScheduledVersionResultSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** Not the one version scheduled to take over next — already in effect, already past, or
         *  still a draft (`discardDraft` covers that case). */
        409: errorBodySchema,
      },
      summary:
        'Cancel the version scheduled to take over next, before its date arrives — the policy falls back to whatever is in force today',
      metadata: manage,
    },
    saveDraft: {
      method: 'PUT',
      path: '/:policyId/draft',
      body: companyPolicyDraftBodySchema,
      responses: {
        200: companyPolicyDetailSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Save the draft of the next version (created from the latest version if none)',
      metadata: manage,
    },
    discardDraft: {
      method: 'DELETE',
      path: '/:policyId/draft',
      responses: {
        200: z.object({ deleted: z.enum(['draft', 'policy']) }),
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Discard the draft; a policy that was never published is deleted with it',
      metadata: manage,
    },
    uploadDraftDocument: {
      method: 'PUT',
      path: '/:policyId/draft/document',
      body: uploadCompanyPolicyDocumentBodySchema,
      responses: {
        200: companyPolicyDetailSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** Not a PDF, invalid base64, or over 10 MB. */
        422: errorBodySchema,
      },
      summary: "Attach (or replace) the draft's PDF",
      // The body carries a whole file as base64, which a model cannot produce.
      metadata: routeMeta('policy:manage', { ai: 'hidden' }),
    },
    removeDraftDocument: {
      method: 'DELETE',
      path: '/:policyId/draft/document',
      responses: {
        200: companyPolicyDetailSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: "Remove the draft's PDF",
      metadata: manage,
    },
    publishPolicy: {
      method: 'POST',
      path: '/:policyId/publish',
      body: publishCompanyPolicyBodySchema,
      responses: {
        200: companyPolicyDetailSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** No draft to publish. */
        409: errorBodySchema,
        /** Incomplete draft, or dated before the version it would replace. */
        422: errorBodySchema,
      },
      summary: 'Publish the draft as the next version',
      metadata: manage,
    },
    setArchived: {
      method: 'POST',
      path: '/:policyId/archive',
      body: z.object({ archived: z.boolean() }),
      responses: {
        200: companyPolicyDetailSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary: 'Withdraw a published policy from employees, or restore it',
      metadata: manage,
    },
    getVersion: {
      method: 'GET',
      path: '/:policyId/versions/:versionId',
      responses: {
        200: companyPolicyVersionSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'One version of a policy, exactly as it was published',
      metadata: manage,
    },
    downloadVersionDocument: {
      method: 'GET',
      path: '/:policyId/versions/:versionId/document',
      responses: {
        200: companyPolicyFileSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: "Download a version's PDF (the draft's included)",
      metadata: routeMeta('policy:manage', { ai: 'hidden' }),
    },
    getAcknowledgementReport: {
      method: 'GET',
      path: '/:policyId/versions/:versionId/acknowledgements',
      query: reportQuerySchema,
      responses: {
        200: z.object({
          counts: acknowledgementCountsSchema,
          acknowledgementDueOn: dateOnlySchema.nullable(),
          items: z.array(acknowledgementReportRowSchema),
        }),
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Who in the audience has and has not acknowledged a version',
      metadata: manage,
    },
    sendReminders: {
      method: 'POST',
      path: '/:policyId/remind',
      body: z.object({}).default({}),
      responses: {
        200: z.object({ notified: z.number().int().nonnegative() }),
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** The current version does not require acknowledgement. */
        409: errorBodySchema,
      },
      summary: 'Remind everyone who has not acknowledged the current version',
      metadata: manage,
    },
  },
  { pathPrefix: '/company-policies' },
);
